import { compact, filter, groupBy, keyBy, map, orderBy } from "lodash-es";

import {
  AssistantProjectStatus,
  type AssistantFollowUpObservable,
  type AssistantProjectObservable,
  type AssistantTaskObservable,
} from "@/store/assistant";
import { isClosedAssistantProjectStatus } from "@/store/assistant/assistant-project-status";
import { formatAgentDate, getAgentTimestamp } from "@/pages/agent/utils/agent-date.utils";
import { isClosedFollowUp } from "@/pages/agent/utils/agent-follow-up.utils";
import { buildAgentSearchableText } from "@/pages/agent/utils/agent-search.utils";
import { isClosedAssistantTaskStatus } from "@/store/assistant/assistant-task-status";
import { ProjectSortKey } from "@/pages/agent/projects/use-agent-projects-search-params";

export enum ProjectSectionKind {
  Active = "active",
  Completed = "completed",
}

export type ProjectCardViewModel = {
  project: AssistantProjectObservable;
  openTasksCount: number;
  closedTasksCount: number;
  taskCount: number;
  previewTasks: AssistantTaskObservable[];
  nextUp: ProjectNextUp | null;
  isClosed: boolean;
  searchableText: string;
};

export type ProjectNextUp = {
  label: string;
  detail: string;
  taskId: string | null;
};

type FollowUpProjectGrouping = {
  projectId: string;
  followUp: AssistantFollowUpObservable;
};

export const DEFAULT_PROJECT_STATUS_FILTER_VALUES = [
  AssistantProjectStatus.Active,
  AssistantProjectStatus.Resolved,
  AssistantProjectStatus.Archived,
];

/**
 * Builds project cards from the project, task, and follow-up sync streams.
 *
 * Follow-ups can point to a project directly or through a task. Resolving both
 * shapes here keeps the rendering layer from needing to know backend linkage details.
 */
export const buildProjectCards = ({
  projects,
  tasks,
  followUps,
}: {
  projects: AssistantProjectObservable[];
  tasks: AssistantTaskObservable[];
  followUps: AssistantFollowUpObservable[];
}) => {
  const tasksWithProject = filter(tasks, task => Boolean(task.projectId));
  const tasksById = keyBy(tasksWithProject, task => task.id);
  const tasksByProjectId = groupBy(tasksWithProject, task => task.projectId ?? "");
  const followUpsByProjectId = groupBy(
    compact(
      map(followUps, followUp => {
        const projectId = getFollowUpProjectId({ followUp, tasksById });

        if (!projectId) {
          return null;
        }

        return {
          projectId,
          followUp,
        } satisfies FollowUpProjectGrouping;
      })
    ),
    followUpProjectGrouping => followUpProjectGrouping.projectId
  );

  return map(projects, project => {
    const projectTasks = tasksByProjectId[project.id] ?? [];
    const openTasks = filter(
      projectTasks,
      task => !isClosedAssistantTaskStatus({ status: task.status })
    );
    const sortedOpenTasks = orderBy(
      openTasks,
      [
        task =>
          getAgentTimestamp({
            value: task.dueAt,
            fallback: Number.POSITIVE_INFINITY,
          }),
        task => getAgentTimestamp({ value: task.updatedAt, fallback: 0 }),
      ],
      ["asc", "desc"]
    );
    const projectFollowUps = map(
      followUpsByProjectId[project.id] ?? [],
      followUpProjectGrouping => followUpProjectGrouping.followUp
    );

    return {
      project,
      openTasksCount: openTasks.length,
      closedTasksCount: projectTasks.length - openTasks.length,
      taskCount: projectTasks.length,
      previewTasks: sortedOpenTasks.slice(0, 3),
      nextUp: getProjectNextUp({ openTasks: sortedOpenTasks, followUps: projectFollowUps }),
      isClosed: isClosedAssistantProjectStatus({ status: project.status }),
      searchableText: buildAgentSearchableText({
        values: [project.title, project.description, project.status],
      }),
    } satisfies ProjectCardViewModel;
  });
};

const getFollowUpProjectId = ({
  followUp,
  tasksById,
}: {
  followUp: AssistantFollowUpObservable;
  tasksById: Record<string, AssistantTaskObservable | undefined>;
}) => {
  if (followUp.projectId) {
    return followUp.projectId;
  }

  if (!followUp.taskId) {
    return null;
  }

  return tasksById[followUp.taskId]?.projectId ?? null;
};

const getProjectNextUp = ({
  openTasks,
  followUps,
}: {
  openTasks: AssistantTaskObservable[];
  followUps: AssistantFollowUpObservable[];
}): ProjectNextUp | null => {
  const openFollowUps = filter(followUps, followUp => !isClosedFollowUp({ followUp }));
  const nextFollowUp = orderBy(
    openFollowUps,
    followUp =>
      getAgentTimestamp({
        value: followUp.scheduledFor ?? followUp.dueAt,
        fallback: Number.POSITIVE_INFINITY,
      }),
    "asc"
  )[0];

  if (nextFollowUp) {
    return {
      label: nextFollowUp.content,
      detail: formatAgentDate({
        value: nextFollowUp.scheduledFor ?? nextFollowUp.dueAt,
        includeTime: true,
      }),
      taskId: null,
    };
  }

  const nextTask = orderBy(
    openTasks,
    [
      task =>
        getAgentTimestamp({
          value: task.dueAt,
          fallback: Number.POSITIVE_INFINITY,
        }),
      task =>
        getAgentTimestamp({
          value: task.updatedAt,
          fallback: 0,
        }),
    ],
    ["asc", "desc"]
  )[0];

  if (!nextTask) {
    return null;
  }

  return {
    label: nextTask.title || "Untitled task",
    detail: formatAgentDate({ value: nextTask.dueAt, includeTime: true }),
    taskId: nextTask.id,
  };
};

export const getSortedProjectCards = ({
  projectCards,
  sortKey,
}: {
  projectCards: ProjectCardViewModel[];
  sortKey: ProjectSortKey;
}) => {
  if (sortKey === ProjectSortKey.Alphabetical) {
    return orderBy(projectCards, projectCard => projectCard.project.title.toLowerCase(), "asc");
  }

  return orderBy(
    projectCards,
    projectCard =>
      getAgentTimestamp({
        value: projectCard.project.lastActivityAt ?? projectCard.project.updatedAt,
        fallback: 0,
      }),
    "desc"
  );
};
